import { useMemo, useState, useRef, useEffect } from 'react'
import {
  scopeByDateRange, championshipsCsv, csvFilename, downloadCsv, columnsFor, prepareContactExport, exportContactBudgetMs,
} from '../../lib/csvExport.js'
import {readCurrentContactSnapshot,contactRecordBinding,CONTACT_SNAPSHOT_LIMIT} from '../../lib/currentChampionshipContacts.js'
import {getSessionGeneration,onSessionGenerationChange} from '../../lib/sessionGeneration.js'
import { WINDOW_STEPS } from '../../lib/championships.js'
import styles from './ExportCsv.module.css'

/**
 * EXPORT CSV — the whole filtered universe, or exactly the rows you ticked.
 *
 * Sits next to the filter bar so the scope it exports is the scope on screen:
 * `items` is the already-filtered list, and the date range narrows it further
 * on the EFFECTIVE event date. The file is the FULL result — the views page for
 * the DOM's sake, the export never does.
 *
 * TWO SCOPES, one of them explicit:
 *
 *  - **Export selected (N)** — the rows checked on a table view, exactly as
 *    checked. The date range does NOT apply: a hand-picked set is already the
 *    answer, and silently dropping half of it to a range would be a lie. This
 *    is the default the moment anything is ticked.
 *  - **Export all (filtered)** — the historic behaviour, and the fallback when
 *    nothing is ticked: every record the active filters + date range resolve to.
 *
 * Both go through the same `CSV_COLUMNS` / `championshipsCsv`, so the two files
 * differ only in which records they contain.
 */

const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`

function shiftDays(now, n) {
  const d = new Date(now)
  d.setHours(0, 0, 0, 0)
  d.setDate(d.getDate() + n)
  return d
}

const FILTER_LABELS = {
  sport: 'sport', level: 'level', state: 'state', product: 'product',
  incumbent: 'incumbent', owner: 'owner', year: 'year', q: 'search',
}

export default function ExportCsv({
  items, total, filters = {}, now = new Date(), viewLabel = '', scopeLabel = '',
  selected = [], onClearSelection,
  // The category space this export belongs to. The file is named for the space
  // it came from, so an RFP export can never be mistaken for a championship one.
  nounPlural = 'championships', slug = 'championships', title = 'championship opportunities',
  // The space's SINGULAR noun — it heads the name column, so an exported RFP or
  // police department is never called a championship in the file itself.
  noun = '',
  // What the space's effective date IS. Championships get the verbatim
  // pre-existing wording; an RFP export ranges on its close date and says so.
  dateLabel = 'Event date',
  /* ⚠️ THE UNIVERSE IS STILL ARRIVING.
   *
   * Police, sampled mid-load, offered "⤓ Export CSV 3,100" and then
   * "⤓ Export CSV 6,800" against a ~26k universe — a quarter of the
   * departments in a file that looks complete, with nothing on the control
   * saying so. The export is legitimate (it is exactly what is loaded), so it
   * is not disabled; it is LABELLED, here and again inside the panel. */
  loading = false,
  incomplete = false,
}) {
  const [open, setOpen] = useState(false)
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [includeUndated, setIncludeUndated] = useState(true)
  const [done, setDone] = useState('')
  const [progress,setProgress] = useState(null)
  const [capturedAt, setCapturedAt] = useState('')
  const operation = useRef(null)
  const cancelExport = () => { operation.current?.abort(); operation.current=null; setProgress(null) }
  const closeExport = () => { cancelExport(); setOpen(false) }
  useEffect(() => onSessionGenerationChange(() => {
    operation.current?.abort(); operation.current=null; setProgress(null); setDone(''); setOpen(false)
  }), [])
  useEffect(() => () => operation.current?.abort(), [])
  useEffect(() => { if(!open){operation.current?.abort();operation.current=null;setProgress(null)} }, [open])
  // null = follow the selection. An explicit pick sticks until it can't.
  const [modePref, setModePref] = useState(null)
  const triggerRef = useRef(null)
  const panelRef = useRef(null)

  // A top-layer dialog cannot be clipped by the filter row or fixed sidebar.
  // Native modal isolation and Escape work for keyboard users too.
  useEffect(() => {
    if (!open) return undefined
    const panel = panelRef.current
    const trigger = triggerRef.current
    panel.showModal()
    return () => {
      panel.close()
      trigger?.focus({ preventScroll: true })
    }
  }, [open])

  const scoped = useMemo(
    () => scopeByDateRange(items, { from, to, includeUndated }),
    [items, from, to, includeUndated]
  )

  const undatedInScope = useMemo(() => items.filter((c) => !c.effDate).length, [items])

  const activeFilters = useMemo(
    () => Object.entries(filters)
      .filter(([, v]) => v !== '' && v != null)
      .map(([k, v]) => `${FILTER_LABELS[k] || k}: ${v}`),
    [filters]
  )

  const setRange = (a, b) => { setFrom(a ? ymd(a) : ''); setTo(b ? ymd(b) : ''); setDone('') }
  const preset = (side, days) => {
    const today = shiftDays(now, 0)
    if (side === 'back') setRange(shiftDays(now, -days), shiftDays(now, -1))
    else setRange(today, shiftDays(now, days))
  }
  const presetYear = () => {
    const y = now.getFullYear()
    setRange(new Date(y, 0, 1), new Date(y, 11, 31))
  }

  // "Selected" is the default whenever rows are ticked; clearing the ticks
  // falls back to the filtered set rather than leaving a dead mode selected.
  const selCount = selected.length
  const mode = modePref === 'filtered' ? 'filtered' : selCount ? 'selected' : 'filtered'
  const rows = mode === 'selected' ? selected : scoped
  const currentExportContext = rows.map(row=>contactRecordBinding(row)).join('\n')
  const latestExportContext = useRef(currentExportContext)
  latestExportContext.current = currentExportContext
  useEffect(() => {
    if (!operation.current) return
    operation.current.abort('scope_changed')
    operation.current = null
    setProgress(null)
    setDone('Selection or record details changed. No file downloaded. Export again from the current view.')
  }, [currentExportContext])

  // A FRESH selection always defaults back to "selected" — an override picked
  // during an earlier selection must not silently govern the next one.
  const hadSelection = useRef(false)
  useEffect(() => {
    if (selCount > 0 && !hadSelection.current) setModePref(null)
    hadSelection.current = selCount > 0
  }, [selCount])

  const doExport = async (lookupContacts) => {
    if(operation.current)return
    const controller=new AbortController(),session=getSessionGeneration(),context=currentExportContext
    const recordCapturedAt = new Date().toISOString()
    setCapturedAt(recordCapturedAt)
    const deadline=setTimeout(()=>controller.abort('deadline'),exportContactBudgetMs(rows.length))
    operation.current=controller;setDone('');setProgress({phase:'Starting',completed:0,total:rows.length})
    const name = mode === 'selected'
      ? csvFilename({ now, prefix: `srchamp-${slug}-selected`, rangeLabel: `selection-${selCount}` })
      : csvFilename({ from, to, now, prefix: `srchamp-${slug}` })
    try{
      const exported=await prepareContactExport(rows,{recordCapturedAt,signal:controller.signal,getSession:getSessionGeneration,lookup:lookupContacts,limit:CONTACT_SNAPSHOT_LIMIT,
        onProgress:value=>{if(operation.current===controller)setProgress(value)},
        readContacts:(ids,{signal,onProgress,rows:captured})=>readCurrentContactSnapshot(ids,{signal,onProgress,readSchool:'explicit',bindings:Object.fromEntries(captured.map(row=>[String(row.id),contactRecordBinding(row)]))}),
      })
      if(controller.signal.aborted||operation.current!==controller||session!==getSessionGeneration())return
      if(context!==latestExportContext.current){setDone('Selection or record details changed. No file downloaded. Export again from the current view.');return}
      const ok=downloadCsv(name,championshipsCsv(exported,now,{noun}))
      setDone(ok?`${exported.length.toLocaleString()} rows → ${name} · Record snapshot captured ${recordCapturedAt}`:'Download unavailable in this context.')
    }catch(error){
      if(operation.current===controller&&session===getSessionGeneration())setDone(controller.signal.reason==='deadline'||error.code==='export_deadline'?'Contact read reached the displayed time budget. No file downloaded. Narrow the selection or export without contact lookup.':error.name==='AbortError'?'Export canceled. No file downloaded.':'Export failed. No file downloaded.')
    }finally{clearTimeout(deadline);if(operation.current===controller){operation.current=null;setProgress(null)}}
  }

  const rangeNote =
    from && to ? `${from} → ${to}`
      : from ? `from ${from}`
        : to ? `through ${to}`
          : 'all dates'

  return (
    <div className={styles.wrap}>
      <button
        type="button"
        ref={triggerRef}
        aria-haspopup="dialog"
        className={`${styles.trigger} ${mode === 'selected' ? styles.triggerSel : ''}`}
        onClick={() => { if(open)closeExport();else setOpen(true) }}
        aria-expanded={open}
        title={mode === 'selected'
          ? `Export the ${selCount.toLocaleString()} selected row${selCount === 1 ? '' : 's'} to CSV`
          : loading
            ? `The ${nounPlural} universe is still loading — an export taken now contains only what has arrived so far, not the whole set`
            : incomplete
              ? 'The record load stopped early. This exports only loaded records, not the complete space.'
            : `Export the current filtered ${nounPlural} to CSV`}
        data-loading={loading ? '1' : '0'}
      >
        {mode === 'selected' ? '⤓ Export selected' : '⤓ Export CSV'}
        <span className={styles.triggerCount}>
          {(mode === 'selected' ? selCount : items.length).toLocaleString()}
        </span>
        {loading && mode !== 'selected' && <span className={styles.triggerPartial}>so far</span>}
        {!loading && incomplete && mode !== 'selected' && <span className={styles.triggerPartial}>partial</span>}
      </button>

      {open && (
        <dialog ref={panelRef} className={styles.panel} aria-label="Export CSV"
          onCancel={closeExport}
          onKeyDown={(event) => {
            if (event.key !== 'Tab') return
            // Native dialogs isolate the page, but boundary Tab can move to
            // browser chrome. Wrap only the boundaries; preserve native date
            // input segments and ordinary tab order within the dialog.
            const controls = [...event.currentTarget.querySelectorAll('button, input, select, textarea, a[href], [tabindex]')]
              .filter((control) => control.tabIndex >= 0 && !control.matches(':disabled') && control.getClientRects().length > 0 && getComputedStyle(control).visibility !== 'hidden')
            const first = controls[0]
            const last = controls.at(-1)
            if (event.shiftKey && document.activeElement === first) {
              event.preventDefault()
              last?.focus()
            } else if (!event.shiftKey && document.activeElement === last) {
              event.preventDefault()
              first?.focus()
            }
          }}
          onClick={(event) => {
            if (event.target !== event.currentTarget) return
            const rect = event.currentTarget.getBoundingClientRect()
            if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) closeExport()
          }}>
          <div className={styles.head}>
            <span className={styles.title}>Export {title}</span>
            <button type="button" className={styles.close} onClick={closeExport} aria-label="Close">×</button>
          </div>

          {/* WHICH ROWS — only offered when something is actually ticked. */}
          {selCount > 0 && (
            <div className={styles.section}>
              <div className={styles.sectionLabel}>Rows</div>
              <div className={styles.modeRow}>
                <button
                  type="button"
                  className={`${styles.mode} ${mode === 'selected' ? styles.modeOn : ''}`}
                  onClick={() => { setModePref('selected'); setDone('') }}
                >
                  Export selected ({selCount.toLocaleString()})
                </button>
                <button
                  type="button"
                  className={`${styles.mode} ${mode === 'filtered' ? styles.modeOn : ''}`}
                  onClick={() => { setModePref('filtered'); setDone('') }}
                >
                  Export all (filtered)
                </button>
                {onClearSelection && (
                  <button
                    type="button"
                    className={styles.linkBtn}
                    onClick={() => { onClearSelection(); setModePref(null); setDone('') }}
                  >
                    Clear selection
                  </button>
                )}
              </div>
            </div>
          )}

          <div className={styles.section} data-dim={mode === 'selected' ? '' : undefined}>
            <div className={styles.sectionLabel}>Date range — effective {dateLabel.toLowerCase()}</div>
            {scopeLabel && <p>Optional additional effective-date filter within the active Worklist periods. Past periods use held dates, which may differ from effective dates.</p>}
            <p>Last N days excludes today; Next N days includes today. Dates are inclusive at both ends.</p>
            <div className={styles.dateRow}>
              <label className={styles.dateField}>
                <span>From</span>
                <input className="input" type="date" value={from} onChange={(e) => { setFrom(e.target.value); setDone('') }} />
              </label>
              <label className={styles.dateField}>
                <span>To</span>
                <input className="input" type="date" value={to} onChange={(e) => { setTo(e.target.value); setDone('') }} />
              </label>
              {(from || to) && (
                <button type="button" className={styles.linkBtn} onClick={() => setRange(null, null)}>Clear range</button>
              )}
            </div>
            <div className={styles.presets}>
              {WINDOW_STEPS.map((n) => (
                <button key={`b${n}`} type="button" className={styles.preset} onClick={() => preset('back', n)}>Last {n}d</button>
              ))}
              {WINDOW_STEPS.map((n) => (
                <button key={`f${n}`} type="button" className={styles.preset} onClick={() => preset('fwd', n)}>Next {n}d</button>
              ))}
              <button type="button" className={styles.preset} onClick={presetYear}>{now.getFullYear()}</button>
              <button type="button" className={styles.preset} onClick={() => setRange(null, null)}>All dates</button>
            </div>
            <label className={styles.check}>
              <input
                type="checkbox"
                checked={includeUndated}
                onChange={(e) => { setIncludeUndated(e.target.checked); setDone('') }}
              />
              Include undated records ({undatedInScope.toLocaleString()} in scope)
            </label>
          </div>

          <div className={styles.section}>
            <div className={styles.sectionLabel}>Scope</div>
            <div className={styles.scope}>
              {scopeLabel && <div data-export-scope>{scopeLabel}</div>}
              {mode === 'selected' ? (
                <div>
                  <strong>{selCount.toLocaleString()}</strong> hand-picked row{selCount === 1 ? '' : 's'} of{' '}
                  {items.length.toLocaleString()} filtered
                  {total != null && total !== items.length ? ` (${total.toLocaleString()} loaded)` : ''}
                </div>
              ) : (
                <div>
                  <strong>{scoped.length.toLocaleString()}</strong> of {items.length.toLocaleString()} filtered
                  {total != null && total !== items.length ? ` (${total.toLocaleString()} loaded)` : ''} — {rangeNote}
                </div>
              )}
              {loading && (
                <div className={styles.scopePartial} role="status">
                  ⚠ Still loading. This exports the {items.length.toLocaleString()} record
                  {items.length === 1 ? '' : 's'} that have arrived so far — not the whole universe.
                  Wait for the load to finish for a complete file.
                </div>
              )}
              {!loading && incomplete && <div className={styles.scopePartial} role="status">Partial load. This exports only the records already loaded. Retry the full load before treating the file as complete.</div>}
              <div className={styles.scopeFilters}>
                {activeFilters.length
                  ? `Active filters — ${activeFilters.join(' · ')}`
                  : loading || incomplete ? 'No filters active — exporting the loaded records only.' : 'No filters active — exporting the full universe.'}
                {viewLabel ? ` · view: ${viewLabel}` : ''}
              </div>
              <div className={styles.scopeNote}>
                {mode === 'selected'
                  ? 'Exactly the rows you checked — the date range above does not apply to a hand-picked selection. '
                  : 'The export is the FULL filtered set, not the visible page. '}
                Columns: {columnsFor(rows, { noun }).map((c) => c.label).join(', ')}.
              </div>
            </div>
          </div>

          <div className={styles.section}>
            <strong>Choose your export: current contacts or basic data without contacts.</strong>
            <p>Current-contact export reads all {rows.length.toLocaleString()} rows in sequential batches of up to {CONTACT_SNAPSHOT_LIMIT}, only when you click its button. Basic export makes no contact reads; contacts are explicitly marked unread.</p>
            <p>Record data is captured when export starts; current contacts are read afterward. Later non-contact record edits are not included in that snapshot.</p>
            <p>Every selected row is retained, including off-screen rows. Direct contacts and school candidates remain separate; candidates are not selected recipients. Missing phone data stays blank. Unread does not mean no contacts found. Contact reads stop after {Math.ceil(exportContactBudgetMs(rows.length)/60000)} minutes with no file downloaded; narrow the selection or export without lookup if needed.</p>
            {rows.length>CONTACT_SNAPSHOT_LIMIT&&<p role="status">All {rows.length.toLocaleString()} rows will be retained. Contact enrichment is optional and may take time: batches run one at a time, with cancel available. Without enrichment, contacts are explicitly marked unread.</p>}
            {progress&&<div role="status">Record snapshot captured {capturedAt}. Reading contacts: {progress.phase} {progress.completed} of {progress.total}. <button type="button" data-export-cancel onClick={()=>{cancelExport();setDone('Export canceled. No file downloaded.')}}>Cancel export</button></div>}
          </div>
          <div className={styles.foot}>
            <button
              type="button"
              className="btn btn-primary"
              data-export-download
              onClick={() => doExport(true)}
              disabled={!rows.length || !!progress}
            >
              {`Export with current contacts (${rows.length.toLocaleString()} rows)`}
            </button>
            <button type="button" className="btn" data-export-basic onClick={() => doExport(false)} disabled={!rows.length || !!progress}>
              {`Export basic data without contacts (${rows.length.toLocaleString()} rows)`}
            </button>
            {done && <span className={styles.done}>{done}</span>}
            {!rows.length && (
              <span className={styles.done}>
                {mode === 'selected' ? 'Nothing selected.' : 'Nothing in this range.'}
              </span>
            )}
          </div>
        </dialog>
      )}
    </div>
  )
}
